import React, { useEffect, useState } from 'react';
import { Users, Package, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

interface Product { id: number; nom: string; }
interface Promotion { id: number; nom: string; product_id: number; }
interface User { id: number; email: string; nom: string; prenom: string; }

interface UserStats {
  progression_globale: number;
  modules_termines: number;
  modules_total: number;
  meilleur_score_quiz: number | null;
  nombre_simulations: number;
  score_moyen_simulations: number | null;
}

interface DashboardData {
  total_agents: number;
  total_products: number;
  total_promotions: number;
  products: Product[];
  promotions: Promotion[];
}

export default function FormateurDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [selectedPromotion, setSelectedPromotion] = useState<string>('');
  const [users, setUsers] = useState<User[]>([]);
  const [unregisteredUsers, setUnregisteredUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userStats, setUserStats] = useState<Record<number, UserStats>>({});

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    if (!selectedProduct && !selectedPromotion) {
      fetchUsers(true);
      fetchUnregisteredUsers();
    } else if (selectedProduct && selectedPromotion) {
      fetchUsers();
      setUnregisteredUsers([]);
    } else {
      setUsers([]);
      setUnregisteredUsers([]);
    }
  }, [selectedProduct, selectedPromotion]);

  const fetchDashboard = async () => {
    try {
      const res = await api.get('/formateur/dashboard');
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  // Clear promotions selection if they don't belong to selected products
  useEffect(() => {
    if (!data) return;
    if (!selectedProduct) return;
    const validIds = data.promotions
      .filter((p) => p.product_id === Number(selectedProduct))
      .map((p) => String(p.id));
    if (selectedPromotion && !validIds.includes(selectedPromotion)) {
      setSelectedPromotion('');
    }
  }, [selectedProduct, data]);


  const fetchUsers = async (all: boolean = false) => {
    setLoadingUsers(true);
    try {
      setUserStats({});
      const params = new URLSearchParams();
      if (!all) {
        if (selectedProduct) params.append('product_ids', selectedProduct);
        if (selectedPromotion) params.append('promotion_ids', selectedPromotion);
      }
      const query = params.toString();
      const res = await api.get(`/formateur/users${query ? `?${query}` : ''}`);
      setUsers(res.data);
      fetchStatsForUsers(res.data);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchStatsForUsers = async (list: User[]) => {
    const entries = await Promise.all(
      list.map(async (u) => {
        try {
          const res = await api.get(`/stats/user/${u.id}`);
          return [u.id, res.data as UserStats] as [number, UserStats];
        } catch {
          return [u.id, null] as [number, UserStats | null];
        }
      })
    );

    setUserStats((prev) => {
      const updated = { ...prev };
      entries.forEach(([id, stat]) => {
        if (stat) {
          updated[id] = stat;
        }
      });
      return updated;
    });
  };

  const fetchUnregisteredUsers = async () => {
    try {
      const res = await api.get('/formateur/unregistered-users');
      setUnregisteredUsers(res.data);
    } catch (e) {
      setUnregisteredUsers([]);
    }
  };


  const handleProductChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProduct(e.target.value);
  };

  const resetFilters = () => {
    setSelectedProduct('');
    setSelectedPromotion('');
  };

  const handlePromotionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPromotion(e.target.value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data) return null;

  const filteredPromotions = selectedProduct
    ? data.promotions.filter((p) => p.product_id === Number(selectedProduct))
    : data.promotions;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white shadow rounded-lg p-6 hover:shadow-md transition-shadow flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">Agents</p>
            <p className="text-2xl font-bold">{data.total_agents}</p>
          </div>
          <Users className="h-8 w-8 text-primary" />
        </div>
        <Link
          to="/formateur/products"
          className="bg-white shadow rounded-lg p-6 hover:shadow-md transition-shadow flex items-center justify-between"
        >
          <div>
            <p className="text-sm font-medium text-gray-500">Produits</p>
            <p className="text-2xl font-bold">{data.total_products}</p>
          </div>
          <Package className="h-8 w-8 text-blue-500" />
        </Link>
        <div className="bg-white shadow rounded-lg p-6 hover:shadow-md transition-shadow flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">Promotions</p>
            <p className="text-2xl font-bold">{data.total_promotions}</p>
          </div>
          <Calendar className="h-8 w-8 text-purple-500" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block mb-1 font-medium">Produits</label>
          <select
            value={selectedProduct}
            onChange={handleProductChange}
            className="w-full border rounded-md p-2 shadow-sm hover:shadow focus:ring-primary focus:border-primary transition"
          >
            <option value="">Tous</option>
            {data.products.map((p) => (
              <option key={p.id} value={p.id}>{p.nom}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-1 font-medium">Promotions</label>
          <select
            value={selectedPromotion}
            onChange={handlePromotionChange}
            className="w-full border rounded-md p-2 shadow-sm hover:shadow focus:ring-primary focus:border-primary transition"
          >
            <option value="">Tous</option>
            {filteredPromotions.map((p) => (
              <option key={p.id} value={p.id}>{p.nom}</option>
            ))}
          </select>
        </div>
      </div>
      <button
        onClick={resetFilters}
        className="mt-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition-colors hover:shadow"
      >
        Réinitialiser les filtres
      </button>

      {loadingUsers ? (
        <div>Chargement...</div>
      ) : (
        users.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 transition-opacity duration-300">
            <h2 className="text-lg font-semibold mb-4">Utilisateurs inscrits</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th rowSpan={2} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom complet</th>
                    <th rowSpan={2} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th colSpan={3} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Formations</th>
                    <th colSpan={2} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Simulations</th>
                    <th rowSpan={2} className="relative px-6 py-3"><span className="sr-only">Afficher les infos</span></th>
                  </tr>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progression globale</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modules terminés</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Meilleur score quiz</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Simulations réalisées</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score moyen</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((u) => {
                    const stats = userStats[u.id];
                    return (
                      <tr key={u.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {u.prenom} {u.nom}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{u.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {stats ? `${stats.progression_globale.toFixed(0)}%` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {stats ? `${stats.modules_termines}/${stats.modules_total}` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {stats && stats.meilleur_score_quiz !== null ? `${stats.meilleur_score_quiz}%` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {stats ? stats.nombre_simulations : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {stats && stats.score_moyen_simulations !== null ? `${Math.round(stats.score_moyen_simulations)}%` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <Link
                            to={`/formateur/users/${u.id}?productId=${selectedProduct}&promotionId=${selectedPromotion}`}
                            className="inline-flex items-center px-3 py-1 border border-primary text-primary rounded-md hover:bg-primary hover:text-white"
                          >
                            Afficher les infos
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}
      {unregisteredUsers.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 transition-opacity duration-300">
          <h2 className="text-lg font-semibold mb-4">Utilisateurs non inscrits</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom complet</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="relative px-6 py-3"><span className="sr-only">Afficher les infos</span></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {unregisteredUsers.map((u) => (
                  <tr key={u.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {u.prenom} {u.nom}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{u.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <Link
                        to={`/formateur/users/${u.id}?productId=${selectedProduct}&promotionId=${selectedPromotion}`}
                        className="inline-flex items-center px-3 py-1 border border-primary text-primary rounded-md hover:bg-primary hover:text-white"
                      >
                        Afficher les infos
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}